<template>
  <div class="site-search scroll-y">
    <ais-instant-search
      v-if="initialized"
      :index-name="$algolia.indexPrefix + 'search'"
      :search-client="$algolia.client"
      :insights="true"
      :future="{preserveSharedStateOnUnmount: true}"
    >
      <div class="container py-lg lg:py-2xl">
        <header class="flex items-center justify-between">
          <h1 class="text-xl">{{ 'How can we help you?' | t }}</h1>
          <button type="button" class="p-0 bg-none" @click="$emit('hide')">
            <SloIcon name="XMark" size="lg" />
          </button>
        </header>
        <div class="tab-wrapper border-none mt-lg mb-md">
          <ul class="nav nav-tabs primary-tabs text-left" role="tablist">
            <li :class="{ active: tab === 'all' }">
              <a href="#all" @click.prevent="tab = 'all'" class="pt-0">{{ 'All' | t }}</a>
            </li>
            <li
              v-for="group in groups"
              :key="group.slug"
              :class="{ active: tab === group.slug }"
            >
              <a
                :href="`#${group.slug}`"
                @click.prevent="tab = group.slug"
                class="pt-0"
              >
                {{ group.name }}
              </a>
            </li>
          </ul>
        </div>
        <div class="search-box">
          <SloIcon name="MagnifyingGlass" />
          <input
            v-model="query"
            ref="input"
            type="search"
            placeholder="Search for something..."
            role="search"
          />
        </div>
        <ais-index :index-name="$algolia.indexPrefix + 'search'" key="global-search-stats">
          <SloConfigure :query="query" />
          <ais-hits class="my-2xl">
            <template v-slot="{ items }">
              <p
                v-if="!items.length && tab === 'all'"
                class="text-base md:text-xl mb-xl"
              >
                {{ 'No results found for:' | t }} {{ query }}
              </p>
              <span v-else></span>
            </template>
          </ais-hits>
        </ais-index>
        <template v-for="group in groups">
          <ais-index
            v-if="tab === 'all' || tab === group.slug"
            :index-name="$algolia.indexPrefix + 'search'"
            :key="group.slug"
            :id="`#${group.slug}`"
          >
            <SloConfigure
              :query="query"
              :filters="group.filters"
              :hits-per-page.camel="
                group.slug === tab ? group.hitsPerPage * 3 : group.hitsPerPage
              "
            />
            <ais-hits class="my-2xl">
              <template v-slot="{ items, sendEvent }">
                <div v-if="items.length">
                  <header class="search-group-header">
                    <h2 class="text-xl">
                      {{ group.name }}
                    </h2>
                    <SearchPagination
                      :tab="tab"
                      :group="group"
                      v-on:view-all="tab = $event"
                      v-if="items.length"
                    />
                  </header>
                  <section
                    :class="`search-results search-results--${group.slug}`"
                  >
                    <SearchResult
                      v-for="item in items"
                      :item="item"
                      :sendEvent="sendEvent"
                      :key="item.objectID"
                      v-on:show-video="showVideo"
                    />
                  </section>
                </div>
                <div v-else-if="tab !== 'all'">
                  <p v-if="tab === group.slug" class="text-base md:text-xl mb-xl">
                    {{ 'No results found for:' | t }} {{ query }}
                  </p>
                  <div
                    v-if="group.slug === 'specs'"
                    class="px-md py-md mt-md lg:mt-sm rounded-md border-1 border-gray-400"
                    style="pointer-events: initial"
                  >
                    <div class="flex-col sm:flex-row sm:items-center">
                      <div class="flex-1">
                        <p class="text-sm optimize-width">
                          <span class="fw-strong"
                          >{{ 'Request a Custom Spec.' | t }}</span
                          >
                          {{ 'Don’t see a variation that matches you’re looking for? You can request a custom spec from us with the exact options you want. Just click the button and fill out the form.' | t }}
                        </p>
                      </div>
                      <div class="mt-md sm:ml-auto md:mr-md sm:mt-0">
                        <a class="text-sm fw-strong" href="/request-a-spec"
                        >{{ 'Request a Custom Spec' | t }}</a
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </ais-hits>
          </ais-index>
        </template>
      </div>
      <div
        class="modal fade video-modal in"
        :style="{ display: 'block' }"
        id="videoModal"
        tabindex="-1"
        role="dialog"
        aria-labelledby="videoModalLabel"
        :aria-hidden="!!vimeoId"
        v-if="vimeoId"
      >
        <div class="modal-dialog modal-lg" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <button
                type="button"
                class="close"
                aria-label="Close"
                @click="hideVideo"
              >
                <span aria-hidden="true">&times;</span>
                <span class="sr-only">Close</span>
              </button>
            </div>
            <div class="modal-body">
              <div class="embed-responsive embed-responsive-16by9">
                <iframe
                  class="embed-responsive-item"
                  :src="'//player.vimeo.com/video/' + vimeoId + '?autoplay=1'"
                  webkitallowfullscreen
                  mozallowfullscreen
                  allowfullscreen
                  v-if="vimeoId"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ais-instant-search>
  </div>
</template>

<script>
import 'bootstrap-sass/assets/javascripts/bootstrap/modal';
import t from '../main/i18n';
import {getLangcode} from '../main/utils';
import components from '../algolia';
import SearchPagination from './components/SearchPagination.vue';
import SearchResult from './components/SearchResult.vue';
import SloIcon from '../../lib/components/SloIcon.vue';
import SloConfigure from '../algolia/SloConfigure.vue';

export default {
  components: {
    ...components,
    SearchPagination,
    SearchResult,
    SloConfigure,
    SloIcon
  },
  props: {
    initialized: Boolean
  },
  data() {
    return {
      tab: this.$route.query.contentType || 'all',
      query: '',
      vimeoId: false,
      groups: [
        {
          name: 'Products',
          slug: 'products',
          filters: 'search_tag:Products',
          hitsPerPage: 6
        },
        {
          name: 'Parts',
          slug: 'parts',
          filters: 'search_tag:Parts',
          hitsPerPage: 4
        },
        {
          name: 'Specs',
          slug: 'specs',
          filters: 'search_tag:Specs',
          hitsPerPage: 4
        },
        {
          name: 'Pages',
          slug: 'pages',
          filters: 'search_tag:Pages',
          hitsPerPage: 6
        },
        {
          name: 'Resources',
          slug: 'resources',
          filters: 'search_tag:Resources',
          hitsPerPage: 6
        },
        {
          name: 'Inspiration',
          slug: 'inspiration',
          filters: 'search_tag:Inspiration',
          hitsPerPage: 4
        },
        {
          name: 'Blog',
          slug: 'blog',
          filters: 'search_tag:Blog',
          hitsPerPage: 4
        },
        {
          name: 'Images',
          slug: 'images',
          filters: 'search_tag:Images',
          hitsPerPage: 4
        },
        {
          name: 'Videos',
          slug: 'videos',
          filters: 'search_tag:Videos',
          hitsPerPage: 4
        }
      ]
    };
  },
  filters: {
    t
  },
  computed: {
    langcode() {
      return getLangcode(this.$route.params.langcode);
    }
  },
  methods: {
    showVideo(vimeoId) {
      this.vimeoId = vimeoId;
    },
    hideVideo() {
      this.vimeoId = false;
    }
  }
};
</script>

<style lang="scss">
@import '../../scss/system';

.site-search {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1045; // above navbar and ie-deprecation-banner (1040); above modals (1050)
  background-color: $color-white;
  pointer-events: none;
  visibility: hidden;
  opacity: 0;
  transition: 0.3s;
  transition-property: opacity, visibility;

  body.search-active & {
    pointer-events: auto;
    visibility: visible;
    opacity: 1;
  }

  .container {
    max-width: 1200px;
  }

  h1 {
    font-weight: 500;
  }

  p.optimize-width {
    max-width: 80ch;
    color: $color-gray-700;
  }

  .fw-strong {
    font-weight: 700;
  }

  .search-box {
    position: relative;
    border-radius: 6px;
  }

  .search-box > .slo-icon {
    width: 1.6rem;
    height: 1.6rem;
    position: absolute;
    left: 1em;
    top: 50%;
    transform: translateY(-50%);
    color: $color-gray-700;
  }

  .search-box input[type='search'] {
    font-size: 1.6rem;
    height: auto;
    padding: 1rem 1rem 1rem 4rem;
    color: $color-gray-900;
    border: 1px solid $color-gray-400;
    border-radius: 4px;
    background-color: transparent;

    &:hover,
    &:focus {
      border-color: $color-blue-400;
    }
  }

  .search-box input[type='search']::placeholder {
    color: $color-gray-500;
  }

  .search-group-header {
    display: flex;
    flex-direction: column;
  }

  .search-group-header h2 {
    margin-bottom: 0.5em;
  }

  .search-results {
    margin-top: 4rem;
    display: grid;
    grid-gap: var(--result-row-gap, 2.5rem) var(--result-col-gap, 2.5rem);
    grid-template-columns: repeat(auto-fill, minmax(min(var(--result-min-width), 100%), 1fr));
  }

  .search-results--products,
  .search-results--parts,
  .search-results--resources,
  .search-results--specs {
    --result-min-width: 400px;
  }

  .search-results--blog,
  .search-results--images,
  .search-results--videos,
  .search-results--inspiration,
  .search-results--pages {
    --result-min-width: 300px;
  }

  .search-results--resources,
  .search-results--specs {
    --result-row-gap: 4rem;
  }

  @media (min-width: 768px) {
    .search-group-header {
      flex-direction: row;
    }
    .search-group-header h2 {
      margin-bottom: 0;
      margin-right: auto;
    }
  }

  @media (min-width: 1024px) {
    .search-results--blog,
    .search-results--images,
    .search-results--inspiration,
    .search-results--pages,
    .search-results--videos {
      --result-min-width: 400px;
      --result-row-gap: 5rem;
    }
  }

  @media (min-width: 1200px) {
    .search-results {
      --result-col-gap: 5rem;
    }
  }
}
</style>
